import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function InflowVsDispatchReport() {
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");
  // Default: last 30 days
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = {};
    if (customerFilter !== "all") params.customer_name = customerFilter;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/reports/inflow-vs-dispatch", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerFilter]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) => (r.customer_name || "").toLowerCase().includes(n));
    }
    return list;
  }, [data, q]);

  const total = data?.total || { inward_jobs: 0, jobs_in_hand: 0, dispatch_jobs: 0 };
  const monthly = data?.monthly || [];
  const maxMonth = Math.max(1, ...monthly.map((m) => Math.max(m.inward, m.dispatched)));

  const exportCSV = () => downloadCSV(
    "order-inflow-vs-dispatch",
    ["Customer Name", "Time Period", "Inward Jobs", "Jobs in Hand", "Dispatch Jobs"],
    rows.map((r) => [r.customer_name, `${from} → ${to}`, r.inward_jobs, r.jobs_in_hand, r.dispatch_jobs])
      .concat([[total.customer_name, `${from} → ${to}`, total.inward_jobs, total.jobs_in_hand, total.dispatch_jobs]])
  );

  const backlogRatio = total.inward_jobs > 0
    ? Math.round((total.dispatch_jobs / total.inward_jobs) * 100)
    : 0;
  const backlogTone = backlogRatio >= 90 ? "ok" : backlogRatio >= 70 ? "warn" : "danger";

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Customer</label>
          <div className="flex items-center gap-1">
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="inflow-customer" className="rounded-sm w-64"><SelectValue placeholder="All customers" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <button data-testid="inflow-clear-customer" onClick={() => setCustomerFilter("all")}
                className="p-1.5 hover:bg-neutral-100 rounded-sm" title="Clear"><X size={14} /></button>
            )}
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">Start Date</label>
          <Input data-testid="inflow-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div>
          <label className="label-caps block mb-1">End Date</label>
          <Input data-testid="inflow-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div className="flex-1 min-w-48">
          <label className="label-caps block mb-1">Search customer</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="inflow-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="e.g. Eugia" />
          </div>
        </div>
        <Button data-testid="inflow-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="inflow-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Inward jobs (period)" value={inr(total.inward_jobs)} tone="ok" />
        <Kpi label="Jobs in hand (now)" value={inr(total.jobs_in_hand)} tone="warn" />
        <Kpi label="Dispatched (period)" value={inr(total.dispatch_jobs)} />
        <Kpi label="Dispatch / Inward ratio" value={`${backlogRatio}%`} tone={backlogTone} />
      </div>

      {/* Monthly trend chart */}
      {monthly.length > 0 && (
        <div className="border border-neutral-200 bg-white p-4 mb-4">
          <div className="label-caps mb-3 text-[10px]">Last {monthly.length} months trend</div>
          <div className="flex items-end gap-2 h-32">
            {monthly.map((m) => (
              <div key={m.period} className="flex-1 flex flex-col items-center gap-1" data-testid={`inflow-month-${m.period}`}>
                <div className="flex items-end gap-0.5 h-24 w-full">
                  <div className="flex-1 bg-emerald-500" style={{ height: `${(m.inward / maxMonth) * 100}%` }} title={`Inward ${m.inward}`}></div>
                  <div className="flex-1 bg-[#FF5A00]" style={{ height: `${(m.dispatched / maxMonth) * 100}%` }} title={`Dispatched ${m.dispatched}`}></div>
                </div>
                <div className="text-[10px] font-mono-num text-neutral-500">{m.period}</div>
                <div className="text-[10px] text-neutral-400"><span className="text-emerald-600">{m.inward}</span> / <span className="text-[#FF5A00]">{m.dispatched}</span></div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-neutral-500">
            <span className="inline-block w-3 h-2 bg-emerald-500 align-middle mr-1"></span> Inward &nbsp;
            <span className="inline-block w-3 h-2 bg-[#FF5A00] align-middle mr-1"></span> Dispatched
          </div>
        </div>
      )}

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="inflow-table">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-left px-4 py-2.5">Customer Name</th>
              <th className="text-left px-4 py-2.5">Time Period</th>
              <th className="text-right px-4 py-2.5">Inward Jobs</th>
              <th className="text-right px-4 py-2.5">Jobs in Hand</th>
              <th className="text-right px-4 py-2.5">Dispatch Jobs</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={5} msg="No customers with activity in this period." />}
            {rows.slice(0, 400).map((r) => (
              <tr key={r.customer_name} className="border-t border-neutral-100 data-row" data-testid={`inflow-row-${r.customer_name}`}>
                <td className="px-4 py-2.5 font-medium">{r.customer_name}</td>
                <td className="px-4 py-2.5 text-xs text-neutral-500">{from} → {to}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(r.inward_jobs)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-amber-700">{inr(r.jobs_in_hand)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-[#FF5A00] font-semibold">{inr(r.dispatch_jobs)}</td>
              </tr>
            ))}
            {rows.length > 400 && (
              <tr><td colSpan={5} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 400 of {rows.length} rows.</td></tr>
            )}
            {/* TOTAL row */}
            <tr className="border-t-2 border-neutral-300 bg-neutral-50 font-bold" data-testid="inflow-total-row">
              <td className="px-4 py-2.5 uppercase tracking-widest text-neutral-900">TOTAL</td>
              <td className="px-4 py-2.5 text-xs text-neutral-500">{from} → {to}</td>
              <td className="px-4 py-2.5 text-right font-mono-num text-emerald-800">{inr(total.inward_jobs)}</td>
              <td className="px-4 py-2.5 text-right font-mono-num text-amber-800">{inr(total.jobs_in_hand)}</td>
              <td className="px-4 py-2.5 text-right font-mono-num text-[#FF5A00]">{inr(total.dispatch_jobs)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        <b>Inward Jobs</b>: date_received (fallback created_at) falls in the selected period.
        <b> Jobs in Hand</b>: current pending snapshot (not dispatched/cancelled).
        <b> Dispatch Jobs</b>: dispatch_date falls in the period.
      </p>
    </div>
  );
}


// ============================================================================
// 13. New vs Repeat Business % (monthly)
// ============================================================================
